import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Clock, ArrowRight } from 'lucide-react';
import { BOOKING_URL, contactInfo, beautyServices, wellnessServices } from '../../data/services';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-longevity/artifacts/hxvjto45_Final.png";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-olive text-light">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" data-testid="footer-logo">
              <img 
                src={LOGO_URL} 
                alt="Aphrodite Med Spa" 
                className="h-12 w-auto mb-6"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </Link>
            <p className="text-light/80 text-sm leading-relaxed mb-6">
              Awakening natural beauty through science, wellness, and elevated aesthetic treatments.
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-now-btn-footer"
              className="btn-luxury inline-flex items-center gap-2 bg-amaranth text-white px-6 py-2.5 rounded-full text-sm font-medium"
            >
              Book Appointment
              <ArrowRight size={16} />
            </a>
          </div>

          {/* Beauty Services */}
          <div>
            <h4 className="font-serif text-lg mb-6">Beauty</h4>
            <ul className="space-y-3">
              {beautyServices.services.slice(0, 6).map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/services/${service.id}`}
                    data-testid={`footer-link-${service.id}`}
                    className="text-light/70 hover:text-light text-sm transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Wellness Services */}
          <div>
            <h4 className="font-serif text-lg mb-6">Wellness</h4>
            <ul className="space-y-3">
              {wellnessServices.services.map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/services/${service.id}`}
                    data-testid={`footer-link-${service.id}`}
                    className="text-light/70 hover:text-light text-sm transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/specials"
                  data-testid="footer-link-specials"
                  className="text-light/70 hover:text-light text-sm transition-colors"
                >
                  Current Specials
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  data-testid="footer-link-faq"
                  className="text-light/70 hover:text-light text-sm transition-colors"
                >
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-lg mb-6">Visit Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-thulian mt-0.5 flex-shrink-0" />
                <div className="text-light/80 text-sm">
                  <p>{contactInfo.address}</p>
                  <p>{contactInfo.suite}</p>
                  <p>{contactInfo.city}</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-thulian flex-shrink-0" />
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="text-light/80 hover:text-light text-sm transition-colors"
                >
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-thulian flex-shrink-0" />
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="text-light/80 hover:text-light text-sm transition-colors"
                >
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Instagram size={18} className="text-thulian flex-shrink-0" />
                <a 
                  href="https://instagram.com/aphrodite_med_spa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-light/80 hover:text-light text-sm transition-colors"
                >
                  {contactInfo.instagram}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-thulian mt-0.5 flex-shrink-0" />
                <div className="text-light/80 text-sm">
                  <p>{contactInfo.hours.weekdays}</p>
                  <p>{contactInfo.hours.saturday}</p>
                  <p>{contactInfo.hours.sunday}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-light/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-light/60 text-xs">
            © {currentYear} Aphrodite Med Spa. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-xs text-light/60">
            <Link to="/privacy" className="hover:text-light transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-light transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
